import { ViewIds } from '@ardoq/api-types';
import {
  toDependencyMapDegreesState,
  toSettableDependencyMapViewSettings,
  type DependencyMapDegreesState,
  type DependencyMapViewSettings,
  type DependencyMapCoreState,
} from '@ardoq/dependency-map';
import { dispatchAction } from '@ardoq/rxbeach';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  type DropdownConfig,
  type SettingsConfig,
  SettingsType,
  updateViewSettings,
} from '@ardoq/view-settings';
import { DropdownOption, DropdownOptionType } from '@ardoq/dropdown-menu';
import { IconName } from '@ardoq/icons';
import { OnViewSettingsUpdate } from '@ardoq/data-model';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import {
  getCollapseChildrenConfig,
  getColorByReferenceConfig,
  includeReferenceParentsSlider,
} from '../settingsControlBuilders';
import { isRecord } from '@ardoq/common-helpers';

type ViewpointModeLeftMenuConfigProps = {
  viewSettings: DependencyMapViewSettings;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  i18nLocale: I18nLocale;
};

const getViewpointModeLeftMenuConfig = ({
  viewSettings,
  onViewSettingsUpdate,
  i18nLocale,
}: ViewpointModeLeftMenuConfigProps): SettingsConfig[] => {
  const { colorByReference, showVertical } = viewSettings;

  const layoutDirectionConfig: DropdownConfig = {
    id: 'layoutDirection',
    label: t(
      i18nLocale,
      'tabview:dependencyMap.layoutDirection',
      'Layout direction'
    ),
    iconName: IconName.SORT_ASC,
    type: SettingsType.DROPDOWN,
    options: [
      {
        label: t(
          i18nLocale,
          'tabview:dependencyMap.showHorizontal',
          'Show horizontal'
        ),
        isActive: !showVertical,
        type: DropdownOptionType.OPTION,
        onClick: () =>
          dispatchAction(
            updateViewSettings({
              viewId: ViewIds.DEPENDENCY_MAP_2,
              settings: {
                showVertical: false,
              } satisfies Partial<DependencyMapViewSettings>,
              persistent: true,
            })
          ),
      } satisfies DropdownOption,
      {
        label: t(
          i18nLocale,
          'tabview:dependencyMap.showVertical',
          'Show vertical'
        ),
        isActive: showVertical,
        type: DropdownOptionType.OPTION,
        onClick: () =>
          dispatchAction(
            updateViewSettings({
              viewId: ViewIds.DEPENDENCY_MAP_2,
              settings: {
                showVertical: true,
              } satisfies Partial<DependencyMapViewSettings>,
              persistent: true,
            })
          ),
      } satisfies DropdownOption,
    ],
  };

  return [
    layoutDirectionConfig,
    getColorByReferenceConfig(
      onViewSettingsUpdate,
      colorByReference,
      i18nLocale
    ),
  ];
};

export type ViewpointModeDependencyMapSettingsControlsDependencies =
  DependencyMapDegreesState & {
    viewSettings: DependencyMapViewSettings;
    viewInstanceId: string;
    isEmptyView: boolean;
    /**
     * The same settings in the form they would be written back in. Consumers
     * that read settings in order to set them again cannot use the in-memory
     * `Infinity`.
     */
    settableViewSettings: DependencyMapViewSettings;
  };

export const toViewpointModeSettingsControlsDependencies = ({
  viewSettings,
  viewModel,
  viewInstanceId,
}: DependencyMapCoreState): ViewpointModeDependencyMapSettingsControlsDependencies => {
  const { nodes, noConnectedComponents, maxTreeDepth } = viewModel;

  return {
    viewSettings,
    viewInstanceId,
    isEmptyView: !nodes.length || noConnectedComponents,
    settableViewSettings: toSettableDependencyMapViewSettings(
      viewSettings,
      maxTreeDepth
    ),
    ...toDependencyMapDegreesState({ viewSettings, viewModel }),
  };
};

const areNumbers = (values: unknown[]): boolean =>
  values.every(value => typeof value === 'number');

const areBooleans = (values: unknown[]): boolean =>
  values.every(value => typeof value === 'boolean');

export const isViewpointModeDependencyMapSettingsControlsDependencies = (
  viewProperties: Record<string, unknown>
): viewProperties is ViewpointModeDependencyMapSettingsControlsDependencies => {
  const { viewSettings } = viewProperties;

  return (
    isRecord(viewSettings) &&
    isRecord(viewProperties.settableViewSettings) &&
    typeof viewProperties.viewInstanceId === 'string' &&
    typeof viewSettings.showVertical === 'boolean' &&
    areNumbers([
      viewSettings.degreesOfParenthood,
      viewSettings.degreesOfChildhood,
      viewSettings.treeDepth,
      viewProperties.referenceParentMaxDepth,
      viewProperties.referenceChildMaxDepth,
      viewProperties.maxTreeDepth,
    ]) &&
    areBooleans([
      viewProperties.isEmptyView,
      viewProperties.canSetParentDegrees,
      viewProperties.canSetChildDegrees,
      viewProperties.canSetTreeDepth,
      viewProperties.hasReferenceParents,
      viewProperties.hasCollapsedNodes,
    ])
  );
};

type ToSettingsControlsArgs = {
  dependencies: ViewpointModeDependencyMapSettingsControlsDependencies;
  i18nLocale: I18nLocale;
};

export const toViewpointModeViewModifiers = ({
  dependencies: { viewSettings },
  i18nLocale,
}: ToSettingsControlsArgs): SettingsConfig[] =>
  getViewpointModeLeftMenuConfig({
    viewSettings,
    onViewSettingsUpdate,
    i18nLocale,
  });

export const toViewpointModeViewActions = ({
  dependencies,
  i18nLocale,
}: ToSettingsControlsArgs): SettingsConfig[] => [
  includeReferenceParentsSlider({
    dependencies,
    showChildDegreesSlider: false,
    i18nLocale,
  }),
  getCollapseChildrenConfig({ dependencies, onViewSettingsUpdate, i18nLocale }),
];
